import initializeNewLikeNotificationFilter from './initialization/initializeNewLikeNotificationFilter';
import initializeNewMailNotificationFilter from './initialization/initializeNewMailNotificationFilter';
import initializeProtocolHandlers from './initializeProtocolHandlers';
import setupMailModuleCommonPostRender from './setupMailModuleCommonPostRender';
import { lazyInitializeActivityFeed } from 'owa-activity-feed';
import { isFeatureEnabled } from 'owa-feature-flags';
import 'owa-getstarted-bootstrap';
import { initializeGetStartedTasksLazy } from 'owa-getstarted';
import { initializeBindStatusLazy } from 'owa-linkedin';
import { lazyLoadSenders } from 'owa-mail-amp-store';
import { lazyGetMailModuleDiagnostics } from 'owa-mail-diagnostics';
import { lazySubscribeToHierarchyNotification } from 'owa-mail-folder-notifications';
import 'owa-mail-list-selection-actions-v2/lib/lazyIndex';
import connectedAccountsNotificationOperation from 'owa-service/lib/operation/connectedAccountsNotificationOperation';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { isSpotlightEnabled, lazyInitializeSpotlight } from 'owa-mail-spotlight';
import { lazyInitializePersonalizationSuggestions } from 'owa-personalization';
import { initializeWhatsNewCardsLazy } from 'owa-whats-new';
import { lazyLoadWorkloadScenarioSettings } from 'owa-workload-scenario-settings';
import {
    lazyfindBulkActionItemAction,
    lazySubscribeToBulkActionNotification,
} from 'owa-bulk-action-store-lazy';
import {
    shouldOwaInitializeSkype,
    lazyBeginSkypeInitialization,
    lazyCreateOnSwcReadyEvent,
} from 'owa-skype';
import {
    isWebPushNotificationSupported,
    lazybootStrapWebPushService,
} from 'owa-webpush-notifications';
import { lazyGovern, lazyEnableGovernReport } from 'owa-shared-post-boot';
import { lazyGetAccountInformation } from 'owa-storage-store';
import {
    isLinkedInViewProfileFeatureFlagEnabled,
    lazyEnsureSecondaryReadingPaneTabHandler,
} from 'owa-mail-reading-pane-store';
import { lazyFetchNewsNotifications } from 'owa-mail-news';
import { isNewsEnabled } from 'owa-favorites';
import { lazyEvaluatePlaywrightMailActions } from 'owa-mail-playwright';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { isGetStartedUser } from 'owa-session-store/lib/utils/isGetStartedUser';
import { lazyInitializePostWeveSignal } from 'owa-activity-feed';
import { lazyInitializeSearch } from 'owa-mail-search';
import { is3SServiceAvailable } from 'owa-search';

export default function setupMailModulePostRender() {
    if (getUserConfiguration().IsMonitoring) {
        lazyEvaluatePlaywrightMailActions.importAndExecute();
    }

    // Subscribe to hierarchy notifications
    lazySubscribeToHierarchyNotification.importAndExecute();

    // tasks in here are governed individually and apply to
    // both mail module and mail deeplink scenarios.
    setupMailModuleCommonPostRender(true);

    if (isFeatureEnabled('fwk-mailtoProtocolHandler')) {
        initializeProtocolHandlers();
    }

    lazyGovern
        .importAndExecute(
            {
                /**
                 * Initialize the singleton diagnostics store to register the
                 * diagnostics listeners if dev tools are enabled OR if the
                 * search feedback feature is enabled (since it relies on data
                 * being pushed to the diagnostics store).
                 */
                condition:
                    isFeatureEnabled('fwk-devTools') || isFeatureEnabled('sea-contextFeedback'),
                task: () => lazyGetMailModuleDiagnostics.importAndExecute(),
            },
            initNotifications,
            {
                condition: isFeatureEnabled('sea-clientWarmup') && is3SServiceAvailable(),
                task: () => lazyInitializeSearch.importAndExecute(),
            },
            {
                condition: isSpotlightEnabled(),
                task: () => lazyInitializeSpotlight.importAndExecute(),
            },
            () => lazyLoadWorkloadScenarioSettings.importAndExecute(),
            {
                condition: isConsumer(),
                task: () => lazyLoadSenders.importAndExecute(),
            },
            () => lazyfindBulkActionItemAction.importAndExecute(),
            () => lazySubscribeToBulkActionNotification.importAndExecute(),
            {
                condition: shouldOwaInitializeSkype(),
                task: () => lazyBeginSkypeInitialization.importAndExecute(),
            },
            // Even if OWA is not responsbile for initializing Skype,
            // Still need listen to SWC ready events and try to intitialize notification settings
            () => lazyCreateOnSwcReadyEvent.importAndExecute(),
            {
                condition: isGetStartedUser(),
                task: () => initializeGetStartedTasksLazy.importAndExecute(),
            },
            {
                condition: isFeatureEnabled('mc-personalization'),
                task: () => {
                    lazyInitializePersonalizationSuggestions.importAndExecute();
                },
                idle: true,
            },
            {
                task: () => initializeWhatsNewCardsLazy.importAndExecute(),
                idle: true,
            },
            {
                condition: isLinkedInViewProfileFeatureFlagEnabled(),
                task: () => initializeBindStatusLazy.importAndExecute(),
                idle: true,
            },
            {
                task: () => lazyInitializeActivityFeed.importAndExecute(),
                idle: true,
            },
            {
                condition: isWebPushNotificationSupported(window),
                task: () => lazybootStrapWebPushService.importAndExecute(window),
                idle: true,
            },
            {
                task: () => lazyGetAccountInformation.importAndExecute(),
                idle: true,
            },
            {
                condition: isNewsEnabled(),
                task: () => lazyFetchNewsNotifications.importAndExecute(),
            },
            {
                condition:
                    isFeatureEnabled('auth-activityFeed') &&
                    isFeatureEnabled('auth-activityFeedWeveSignal'),
                task: () => lazyInitializePostWeveSignal.importAndExecute(),
                idle: true,
            },
            {
                condition: isFeatureEnabled('rp-projectionPopout'),
                task: () => lazyEnsureSecondaryReadingPaneTabHandler.importAndExecute(),
                idle: true,
            }
        )
        .then(() => {
            // enable reporting after we've queued everything
            lazyEnableGovernReport.importAndExecute();
        });
}

function initNotifications() {
    initializeNewMailNotificationFilter();
    initializeNewLikeNotificationFilter();

    // this will try the notifications to sync now
    connectedAccountsNotificationOperation({ isOWALogon: true });
}
